import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  School,
  Stethoscope,
  Users,
  DollarSign,
  ArrowRight,
  CheckCircle,
  Heart
} from 'lucide-react';

const Providerships = () => {
  const providershipTypes = [
    {
      title: "For Schools",
      description: "Become an enrollment hub and improve student health outcomes",
      icon: School,
      href: "/for-schools",
      benefits: [
        "Improved student attendance and performance",
        "Enhanced school reputation and parent satisfaction",
        "Access to health education resources",
        "Support for school health programs"
      ],
      color: "primary"
    },
    {
      title: "AMHAS Urgent Care Facilities",
      description: "Vertically integrated urgent care centers providing 100% coverage for enrolled students",
      icon: Stethoscope,
      href: "/urgent-care",
      benefits: [
        "World-class facilities with Western standards",
        "24/7 access, 365 days a year",
        "Full vertical integration of services",
        "100% coverage for all services"
      ],
      color: "accent"
    },
    {
      title: "For NGOs & Government",
      description: "Align with national health strategies and expand your reach",
      icon: Users,
      href: "/for-ngos",
      benefits: [
        "Program integration opportunities",
        "Data sharing and reporting",
        "Rural and low-income targeting",
        "Policy alignment support"
      ],
      color: "secondary"
    },
    {
      title: "CSR & Investors",
      description: "Sponsor cohorts and fund prevention programs",
      icon: DollarSign,
      href: "/csr-investors",
      benefits: [
        "Measurable social impact",
        "Co-branded programs",
        "Transparent reporting",
        "Scalable investment opportunities"
      ],
      color: "primary"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Working with AMHAS</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              AMHAS is vertically integrated with our urgent care facilities, ensuring world-class care delivery
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Provider */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Why Provider with AMHAS?</h2>
            <p className="section-subtitle">
              Together, we can create a sustainable healthcare ecosystem that benefits everyone
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Collective Impact</h3>
              <p className="text-lg text-gray-600 mb-6">
                By working together, we can reach more children, improve health outcomes,
                and create lasting change in communities across Cameroon & The Gambia.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Scale your impact through our network</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Access to comprehensive data and reporting</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Support for your existing programs</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Simple Operations</h3>
              <p className="text-lg text-gray-600 mb-6">
                We handle insurance ops, AI + telehealth platform, and digital claims so providers can focus on students and care.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Streamlined enrollment and management</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Digital tools and support</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Training and capacity building</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Providership Types */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Providership Opportunities</h2>
            <p className="section-subtitle">
              Choose the providership that aligns with your organization's goals
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {providershipTypes.map((providership, index) => {
              const Icon = providership.icon;
              return (
                <motion.div
                  key={providership.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start space-x-4 mb-6">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${providership.color === 'primary' ? 'bg-primary-100' :
                      providership.color === 'accent' ? 'bg-accent-100' :
                        'bg-secondary-100'
                      }`}>
                      <Icon size={32} className={
                        providership.color === 'primary' ? 'text-primary-600' :
                          providership.color === 'accent' ? 'text-accent-600' :
                            'text-secondary-600'
                      } />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{providership.title}</h3>
                      <p className="text-gray-600">{providership.description}</p>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {providership.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-start space-x-3">
                        <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={providership.href}
                    className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold"
                  >
                    <span>Learn More</span>
                    <ArrowRight size={16} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Providership Process */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">How to Become a Provider</h2>
            <p className="section-subtitle">
              Simple steps to join our network and start making an impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Express Interest", description: "Contact us or fill out our providership form" },
              { step: "2", title: "Initial Discussion", description: "We'll discuss your goals and how we can work together" },
              { step: "3", title: "Providership Agreement", description: "Sign a formal agreement outlining our collaboration" },
              { step: "4", title: "Launch & Support", description: "We provide training and ongoing support for your success" }
            ].map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Provider?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Join our network of schools, clinics, NGOs, and investors working together
              to improve child health across Cameroon & The Gambia.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Providerships;
